/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
/* oxlint-disable jsx-a11y/media-has-caption -- Recordings can exist without a transcript; playback controls below are keyboard accessible. */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import styles from "./RecordingAudioPlayer.module.css";

function clock(value: number): string {
  const seconds = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  const minutes = Math.floor(seconds / 60);
  return `${minutes >= 60 ? `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}` : minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

export const RecordingAudioPlayer = forwardRef<
  HTMLAudioElement,
  {
    src: string;
    onTimeUpdate: () => void;
  }
>(function RecordingAudioPlayer({ src, onTimeUpdate }, ref) {
  const { t } = useTranslation();
  const media = useRef<HTMLAudioElement>(null);
  const options = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const closeOutside = (event: PointerEvent): void => {
      if (event.target instanceof Node && !options.current?.contains(event.target) && options.current)
        options.current.open = false;
    };
    const closeEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && options.current?.open) {
        options.current.open = false;
        options.current.querySelector("summary")?.focus({ preventScroll: true });
      }
    };
    document.addEventListener("pointerdown", closeOutside, true);
    document.addEventListener("keydown", closeEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside, true);
      document.removeEventListener("keydown", closeEscape);
    };
  }, []);
  useImperativeHandle(ref, () => media.current!, []);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [rate, setRate] = useState(1);
  const [failed, setFailed] = useState(false);
  const rangeStyle = (percent: number) =>
    ({ "--played": `${percent}%` }) as CSSProperties;
  const syncDuration = () =>
    setDuration(
      Number.isFinite(media.current?.duration) ? media.current!.duration : 0,
    );
  const seek = (value: number) => {
    if (!media.current || duration <= 0) return;
    media.current.currentTime = Math.min(duration, Math.max(0, value));
    setPosition(media.current.currentTime);
    onTimeUpdate();
  };
  return (
    <div>
      <audio
        ref={media}
        src={src}
        preload="metadata"
        onLoadedMetadata={syncDuration}
        onDurationChange={syncDuration}
        onTimeUpdate={() => {
          setPosition(media.current?.currentTime ?? 0);
          onTimeUpdate();
        }}
        onPlay={() => {
          setPlaying(true);
          setFailed(false);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onVolumeChange={() => {
          setMuted(media.current?.muted ?? false);
          setVolume(media.current?.volume ?? 1);
        }}
        onRateChange={() => setRate(media.current?.playbackRate ?? 1)}
        onError={() => {
          setFailed(true);
          setPlaying(false);
        }}
      />
      <div
        className={styles.player}
        role="group"
        aria-label={t("instance.audio.player")}
      >
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t(
            playing ? "instance.audio.pause" : "instance.audio.play",
          )}
          onClick={() => {
            const audio = media.current;
            if (!audio) return;
            if (!audio.paused) audio.pause();
            else
              void audio.play().catch((error: unknown) => {
                if (
                  !(
                    error instanceof DOMException && error.name === "AbortError"
                  )
                )
                  setFailed(true);
              });
          }}
        >
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            {playing ? (
              <path d="M7 5h4v14H7zm6 0h4v14h-4z" />
            ) : (
              <path d="m8 5 11 7-11 7z" />
            )}
          </svg>
        </button>
        <span className={styles.time}>
          {clock(position)} / {clock(duration)}
        </span>
        <input
          type="range"
          className={`${styles.range} ${styles.seek}`}
          aria-label={t("instance.audio.seek")}
          aria-valuetext={`${clock(position)} / ${clock(duration)}`}
          min="0"
          max={duration || 1}
          step="0.1"
          value={Math.min(position, duration || 1)}
          disabled={duration <= 0 || failed}
          style={rangeStyle(
            duration > 0 ? Math.min(100, (position / duration) * 100) : 0,
          )}
          onChange={(event) => seek(Number(event.target.value))}
        />
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t(
            muted || volume === 0
              ? "instance.audio.unmute"
              : "instance.audio.mute",
          )}
          aria-pressed={muted || volume === 0}
          onClick={() => {
            if (media.current) {
              if (media.current.muted || media.current.volume === 0) {
                media.current.muted = false;
                if (media.current.volume === 0) media.current.volume = 1;
              } else media.current.muted = true;
              setMuted(media.current.muted);
              setVolume(media.current.volume);
            }
          }}
        >
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 5 6 9H3v6h3l5 4Z" />
            {muted || volume === 0 ? (
              <path d="m16 9 5 6m0-6-5 6" />
            ) : (
              <path d="M15 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14" />
            )}
          </svg>
        </button>
        <details ref={options} className={styles.options}>
          <summary
            className={styles.iconButton}
            aria-label={t("instance.audio.options")}
          >
            <svg
              aria-hidden="true"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <circle cx="12" cy="5" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="19" r="1.5" />
            </svg>
          </summary>
          <div className={styles.menu}>
            <label>
              {t("instance.audio.speed")}
              <select
                aria-label={t("instance.audio.speed")}
                value={rate}
                onChange={(event) => {
                  if (media.current) {
                    media.current.playbackRate = Number(event.target.value);
                    setRate(Number(event.target.value));
                  }
                }}
              >
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map((value) => (
                  <option key={value} value={value}>
                    {value}×
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.volumeOption}>
              {t("instance.audio.volume")}
              <input
                type="range"
                className={styles.range}
                aria-label={t("instance.audio.volume")}
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                style={rangeStyle(muted ? 0 : volume * 100)}
                onChange={(event) => {
                  if (!media.current) return;
                  media.current.volume = Number(event.target.value);
                  media.current.muted = false;
                  setVolume(media.current.volume);
                  setMuted(false);
                }}
              />
            </label>
          </div>
        </details>
      </div>
      {failed && (
        <p role="alert" className={styles.error}>
          {t("instance.audio.error")}
        </p>
      )}
    </div>
  );
});
